import React, { useState, useEffect } from 'react';
import { useUser } from '../../context/UserContext';
import { API_BASE_URL } from '../../utils/constants';
import { FaStethoscope, FaHome, FaVideo, FaPhone, FaComments, FaDollarSign, FaClock, FaSave, FaPlus, FaMinus } from 'react-icons/fa';
import { toast } from 'react-toastify';
import DashboardLayout from '../../components/layout/DashboardLayout';
import './ConsultationFeeScreen.css';

const ConsultationFeeScreen = () => {
  const { user, setUser } = useUser();
  const [consultationData, setConsultationData] = useState([]);
  const [loading, setLoading] = useState(false);

  const consultationTypes = [
    {
      key: 'inClinic',
      label: 'In-Clinic Consultation',
      description: 'Face-to-face consultation at your clinic',
      icon: <FaStethoscope />,
    },
    {
      key: 'homeVisit',
      label: 'Home Visit',
      description: 'Visit patient at their home',
      icon: <FaHome />,
    },
    {
      key: 'videoConsultation',
      label: 'Video Consultation',
      description: 'Online video call consultation',
      icon: <FaVideo />,
    },
    {
      key: 'teleConsultation',
      label: 'Phone Consultation',
      description: 'Consultation over phone call',
      icon: <FaPhone />,
    },
    {
      key: 'chatConsultation',
      label: 'Chat Consultation',
      description: 'Text-based consultation',
      icon: <FaComments />,
    },
  ];

  useEffect(() => {
    initializeConsultationData();
  }, [user]);

  const initializeConsultationData = () => {
    const initialData = consultationTypes.map((type) => {
      const userService = user?.services?.find((service) => service.type === type.key);
      return {
        key: type.key,
        label: type.label,
        description: type.description,
        icon: type.icon,
        fee: userService?.fee || '',
        duration: userService?.duration || 30,
        enabled: !!userService,
      };
    });
    setConsultationData(initialData);
  };

  const handleFeeChange = (key, value) => {
    if (value && (isNaN(value) || value < 0)) {
      toast.error('Please enter a valid fee greater than or equal to 0.');
      return;
    }
    setConsultationData((prevData) =>
      prevData.map((item) => (item.key === key ? { ...item, fee: value } : item))
    );
  };

  const handleDurationChange = (key, value) => {
    const numValue = parseInt(value) || 1;
    if (numValue < 1) return;
    
    setConsultationData((prevData) =>
      prevData.map((item) => (item.key === key ? { ...item, duration: numValue } : item))
    );
  };

  const adjustDuration = (key, increment) => {
    setConsultationData((prevData) =>
      prevData.map((item) => {
        if (item.key === key) {
          const newDuration = Math.max(1, (item.duration || 1) + increment);
          return { ...item, duration: newDuration };
        }
        return item;
      })
    );
  };

  const toggleConsultationType = (key) => {
    setConsultationData((prevData) =>
      prevData.map((item) =>
        item.key === key
          ? { ...item, enabled: !item.enabled, fee: !item.enabled ? item.fee : '' }
          : item
      )
    );
  };

  const handleSave = async () => {
    const enabledServices = consultationData.filter((item) => item.enabled);
    
    if (enabledServices.length === 0) {
      toast.error('Please enable at least one consultation type.');
      return;
    }

    const invalidServices = enabledServices.filter((item) => !item.fee || item.fee <= 0);
    if (invalidServices.length > 0) {
      toast.error('Please set valid fees for all enabled consultation types.');
      return;
    }

    const finalServiceList = enabledServices.map((item) => ({
      type: item.key,
      fee: parseFloat(item.fee) || 0,
      duration: item.duration || 30,
    }));

    try {
      setLoading(true);
      const accessToken = localStorage.getItem('ACCESS_TOKEN');
      const response = await fetch(`${API_BASE_URL}api/doctor/profile/update`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ services: finalServiceList }),
      });

      if (response.ok) {
        setUser({
          ...user,
          services: finalServiceList,
        });
        toast.success('Consultation fees updated successfully!');
      } else {
        throw new Error('Failed to save consultation fees');
      }
    } catch (error) {
      toast.error('Failed to update consultation fees. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <DashboardLayout title="Consultation Fees" showBackButton={true}>
      <div className="consultation-fee-container">
        <div className="consultation-fee-header">
          <h1>Manage Consultation Fees</h1>
          <p>Select the types of consultations you provide and set your fees. Use the toggle to enable or disable a consultation type.</p>
        </div>

        <div className="consultation-types-list">
          {consultationData.map((type) => (
            <div key={type.key} className={`consultation-card ${type.enabled ? 'enabled' : 'disabled'}`}>
              <div className="card-header">
                <div className="card-title-section">
                  <div className="consultation-icon">{type.icon}</div>
                  <div className="title-info">
                    <h3 className="card-title">{type.label}</h3>
                    <p className="card-description">{type.description}</p>
                  </div>
                </div>
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={type.enabled}
                    onChange={() => toggleConsultationType(type.key)}
                  />
                  <span className="slider"></span>
                </label>
              </div>

              {type.enabled && (
                <div className="card-content">
                  <div className="input-section">
                    <div className="input-group">
                      <label className="input-label">
                        <FaDollarSign className="label-icon" />
                        Consultation Fee (₹)
                      </label>
                      <div className="fee-input-wrapper">
                        <span className="currency-symbol">₹</span>
                        <input
                          type="number"
                          placeholder="Enter fee"
                          value={type.fee}
                          onChange={(e) => handleFeeChange(type.key, e.target.value)}
                          className="fee-input"
                          min="0"
                        />
                      </div>
                      <small className="input-note">Fee is in Indian Rupees (INR)</small>
                    </div>

                    <div className="input-group">
                      <label className="input-label">
                        <FaClock className="label-icon" />
                        Duration (minutes)
                      </label>
                      <div className="duration-controls">
                        <button
                          type="button"
                          className="duration-btn"
                          onClick={() => adjustDuration(type.key, -5)}
                        >
                          <FaMinus />
                        </button>
                        <input
                          type="number"
                          value={type.duration}
                          onChange={(e) => handleDurationChange(type.key, e.target.value)}
                          className="duration-input"
                          min="1"
                        />
                        <button
                          type="button"
                          className="duration-btn"
                          onClick={() => adjustDuration(type.key, 5)}
                        >
                          <FaPlus />
                        </button>
                      </div>
                      <small className="input-note">Duration is in minutes</small>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        <button
          className="save-fees-btn"
          onClick={handleSave}
          disabled={loading}
        >
          <FaSave />
          {loading ? 'Saving...' : 'Save Consultation Fees'}
        </button>

        <div className="footer-note">
          <p>Note: Make sure to save your changes after updating the fees.</p>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default ConsultationFeeScreen;